import { WAC_TO_CPAPI_LOCALE_MAP } from '@/utils/content-queries/constants';
import { SupportedLocales } from '@/utils/localization';

type State = 'draft' | 'published';

const REQUEST_TIMEOUT_MS = 5000;
/** One retry — see the comment on the request loop below. */
const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 500;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const getLiveComposition = async (
	queryParams: URLSearchParams,
	route: string,
	currLocale: SupportedLocales = 'en-US',
) => {
	const projectId = queryParams.get('projectId');
	const compositionId = queryParams.get('compositionId');
	const compositionState: State = (queryParams.get('compositionState') as State) ?? 'draft';
	const contentfulEnv = queryParams.get('contentfulEnv') ?? 'master';
	const contentfulState: State = (queryParams.get('contentfulState') as State) ?? 'draft';
	const locale = queryParams.get('locale') ?? WAC_TO_CPAPI_LOCALE_MAP[currLocale] ?? 'en-US';
	const pageUrl = route.startsWith('/') ? route : `/${route}`;

	const wacmanParams = new URLSearchParams({
		projectId: projectId ?? '',
		compositionId: compositionId ?? '',
		compositionState,
		contentfulEnv,
		contentfulState,
		locale,
		pageUrl,
		hydrate: 'true',
	});

	const baseEndpoint = process.env['WAC_MANAGEMENT_API_BASE_URL'];
	if (!baseEndpoint) {
		console.warn('getLiveComposition: WAC_MANAGEMENT_API_BASE_URL is not configured');
		return undefined;
	}

	const endpoint = `${baseEndpoint}/api/v1/uniform/composition/get?${wacmanParams.toString()}`;

	// A composition that is not already cached in S3 has to be regenerated before
	// wac-management-service can answer, and a cold regeneration regularly takes
	// longer than REQUEST_TIMEOUT_MS. The first request then aborts and the page
	// renders blank with nothing but a console warning — the "reload and it works"
	// behaviour. One retry lands after that regeneration has finished.
	//
	// Only a timeout or a 5xx is retried. A 4xx means the request itself is wrong
	// (unknown compositionId, bad project) and asking again cannot change that.
	for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
		const isLastAttempt = attempt === MAX_ATTEMPTS;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

		try {
			const response = await fetch(endpoint, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
				signal: controller.signal,
			});
			clearTimeout(timeoutId);
			if (!response.ok) {
				const retryable = response.status >= 500;
				if (retryable && !isLastAttempt) {
					console.warn(
						`getLiveComposition: wac-management-service responded ${response.status}, retrying`,
					);
					await delay(RETRY_DELAY_MS);
					continue;
				}
				console.warn(`getLiveComposition: wac-management-service responded ${response.status}`);
				return undefined;
			}
			const result = await response.json();
			if (result.error) {
				console.warn('getLiveComposition: error in response:', result.error);
			}
			return result.composition;
		} catch (e) {
			clearTimeout(timeoutId);
			const err = e instanceof Error ? e : new Error(String(e));
			if (!isLastAttempt) {
				console.warn(`getLiveComposition: fetch failed - ${err.name}: ${err.message}, retrying`);
				await delay(RETRY_DELAY_MS);
				continue;
			}
			console.warn(`getLiveComposition: fetch failed - ${err.name}: ${err.message}`);
			return undefined;
		}
	}

	return undefined;
};
